// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { HardHat, Loader2, RefreshCw, ShieldCheck, ClipboardCheck } from "lucide-react";

const sc = (v) => (v == null ? "text-slate-300" : v >= 90 ? "text-emerald-600" : v >= 75 ? "text-amber-600" : "text-red-600");

export default function SiteInspectionsOverview() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [busy, setBusy] = useState(false);
  const load = async (w = weeks) => {
    setBusy(true);
    try { const { data: d } = await api.get(`/site-inspections/trends?weeks=${w}`); setData(d); } catch (e) { toast.error(e?.detailText || e.message); }
    setBusy(false);
  };
  useEffect(() => { load(weeks); }, [weeks]); // eslint-disable-line
  if (!data) return <div className="flex justify-center py-20" data-testid="sio-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  const maxN = Math.max(1, ...data.weekly.map((w) => w.count));
  return (
    <div className="space-y-5" data-testid="sio-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><HardHat className="h-5 w-5 text-amber-600" />{zh ? "现场巡检总览 · 质量 / 安全评分趋势" : "Site Inspections Overview"}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{zh ? `近 ${data.weeks} 周共 ${data.total} 次巡检 · 按项目与分包的质量 / 安全得分` : `${data.total} inspections in ${data.weeks} weeks`}</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="sio-weeks" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="text-xs border border-slate-200 rounded-lg px-2 py-2 bg-white">{[4, 8, 12, 26].map((w) => <option key={w} value={w}>{zh ? `近 ${w} 周` : `${w} weeks`}</option>)}</select>
          <button data-testid="sio-refresh" onClick={() => load()} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="sio-weekly">
        <p className="text-xs font-bold text-slate-700 mb-3">{zh ? "每周巡检次数与平均得分" : "Weekly"}</p>
        {data.weekly.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="sio-weekly-empty">{zh ? "暂无巡检数据 · 请项目经理在项目指挥中心点「现场巡检」开始记录" : "No data yet"}</p> : (
          <div className="flex items-end gap-2 h-36 overflow-x-auto">
            {data.weekly.map((w) => (
              <div key={w.week} className="flex flex-col items-center min-w-[64px]" data-testid={`sio-week-${w.week}`}>
                <div className="flex items-end gap-0.5 h-24">
                  <div className="w-3 rounded-t bg-slate-300" style={{ height: `${(w.count / maxN) * 100}%` }} title={`${w.count} 次`} />
                  <div className={`w-3 rounded-t ${w.quality == null ? "bg-slate-100" : w.quality >= 90 ? "bg-violet-500" : w.quality >= 75 ? "bg-amber-400" : "bg-red-500"}`} style={{ height: `${w.quality || 0}%` }} title={`质量 ${w.quality ?? "—"}`} />
                  <div className={`w-3 rounded-t ${w.safety == null ? "bg-slate-100" : w.safety >= 90 ? "bg-emerald-500" : w.safety >= 75 ? "bg-amber-400" : "bg-red-500"}`} style={{ height: `${w.safety || 0}%` }} title={`安全 ${w.safety ?? "—"}`} />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{w.week.slice(5)}</p>
                <p className="text-[10px] text-slate-400">{w.count}次 · Q{w.quality ?? "—"} S{w.safety ?? "—"}</p>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">{zh ? "灰=巡检次数 · 紫=质量平均分 · 绿=安全平均分（低于 75 转红）" : "grey count · violet quality · green safety"}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sio-projects">
          <p className="text-xs font-bold text-slate-700 px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5 text-violet-600" />{zh ? "项目得分（低分优先）" : "Projects"}</p>
          {data.projects.length === 0 && <p className="text-sm text-slate-400 text-center py-8">{zh ? "暂无" : "None"}</p>}
          {data.projects.map((p) => (
            <button key={p.projectId} data-testid={`sio-proj-${p.projectId}`} onClick={() => nav(`/project/${p.projectId}/inspection`)} className="w-full text-left px-4 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50 flex items-center gap-3">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 break-words sm:truncate">{p.address}</p><p className="text-[11px] text-slate-400">{p.count} {zh ? "次 · 最近" : "insp · last"} {p.last} · {p.fails} {zh ? "项不合格" : "issues"}</p></div>
              <span className={`text-sm font-bold ${sc(p.quality)}`}>Q{p.quality ?? "—"}</span><span className={`text-sm font-bold ${sc(p.safety)}`}>S{p.safety ?? "—"}</span>
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sio-subs">
          <p className="text-xs font-bold text-slate-700 px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />{zh ? "分包 / 班组表现（低分优先）" : "Subcontractors"}</p>
          {data.subs.length === 0 && <p className="text-sm text-slate-400 text-center py-8">{zh ? "暂无" : "None"}</p>}
          {data.subs.map((s) => (
            <div key={s.sub} data-testid={`sio-sub-${s.sub}`} className="px-4 py-2 border-b border-slate-50 last:border-0 flex items-center gap-3">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 break-words sm:truncate">{s.sub}</p><p className="text-[11px] text-slate-400">{s.projects} {zh ? "个项目 ·" : "projects ·"} {s.checks} {zh ? "项检查 ·" : "checks ·"} {s.fails} {zh ? "项不合格" : "fails"}</p></div>
              <span className={`text-sm font-bold ${sc(s.score)}`}>{s.score ?? "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
